import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import styles from './Form.scss';
import classNames from 'classnames';
import parseServerValidationErrors from '../../../sources/parseServerValidationErrors';

export default class Form extends Component {
    static propTypes = {
        fieldValues: PropTypes.object.isRequired,
        clientValidate: PropTypes.func.isRequired,
        serverError: PropTypes.object,
        processing: PropTypes.bool,
        onFieldChange: PropTypes.func.isRequired,
        onSubmit: PropTypes.func.isRequired,
        children: PropTypes.any.isRequired,
        validateOptions: PropTypes.object,
        onErrors: PropTypes.func,
        onScrollToTop: PropTypes.func,
        onScrollToTopGlobal: PropTypes.func,
        scrollOnError: PropTypes.bool,
        resetAfterSubmit: PropTypes.bool,
    };

    static defaultProps = {
        onErrors: () => {
        },
        validateOptions: {},
        resetAfterSubmit: false,
        scrollOnError: false,
    };

    state = {
        wasSubmit: false,
        editedAfterSubmit: false
    };

    componentWillReceiveProps(next) {
        if (next.resetAfterSubmit && this.wasSubmitSuccess(next)) {
            this.setState({
                wasSubmit: false,
                editedAfterSubmit: false,
            });
        }
    }

    wasSubmitSuccess = (next) => {
        return !next.processing && this.props.processing && !next.serverError;
    }

    getValidationErrors = () => {
        const {editedAfterSubmit, wasSubmit} = this.state;
        const {serverError} = this.props;

        const clientErrors = wasSubmit ? this.getClientValidationErrors() : {};
        const serverErrors = _.mapValues(parseServerValidationErrors(serverError), i => i.label);

        const validationErrors = {...clientErrors};
        if (!editedAfterSubmit)
            Object.assign(validationErrors, serverErrors);

        return validationErrors;
    };

    render() {
        const {className, processing} = this.props;
        const validationErrors = this.getValidationErrors();

        const formClassName = classNames(className, styles.form);

        return (
            <form className={formClassName} onSubmit={this.handleSubmit} onKeyDown={this.handleKeyDown} ref='form'>
                {Array.isArray(this.props.children)
                    ? this.props.children.map(
                        this.renderFormField(processing || validationErrors),
                    )
                    : [this.props.children].map(
                        this.renderFormField(processing || validationErrors),
                    )}
            </form>
        );
    }

    getClientValidationErrors() {
        const {clientValidate, fieldValues, validateOptions} = this.props;

        return clientValidate(fieldValues, validateOptions);
    }

    hasClientValidationErrors() {
        const {onScrollToTopGlobal, scrollOnError, onScrollToTop} = this.props;
        const errors = this.getClientValidationErrors();
        const errorsKeys = Object.keys(errors);
        if (scrollOnError) {
            const node = ReactDOM.findDOMNode(this.refs[errorsKeys[0]]);
            const scrollToPosition = node ? node.offsetTop - 100 : 0;
            const onScroll = onScrollToTop || onScrollToTopGlobal;
            onScroll(scrollToPosition);
        }

        return Object.values(errors)
            .some(v => Boolean(v))
    }

    renderFormField = validationErrors => (field, index) => {
        if (!field)
            return field;

        if (Array.isArray(field)) {
            return field.map(this.renderFormField(validationErrors).bind(this))
        }

        const {fieldValues} = this.props;

        const props = {
            key: index,
            autoFocus: index === 0,
        };

        if (field.props.type === 'submit') {
            props.onClick = this.handleSubmit;
            props.disabled = field.props.disabled || this.props.processing;
        }

        if (field.props.fieldKey !== undefined) {
            const {fieldKey} = field.props;

            props.value = fieldValues[fieldKey];
            props.error = validationErrors[fieldKey] || field.props.error;
            if (!field.props.onChange) {
                props.onChange = this.handleFieldChange(fieldKey);
            }
            props.ref = fieldKey;
        }

        if (field.props.type === 'FormFieldWrapper') {
            const {fieldKey} = field.props;
            props.value = fieldValues;
            props.error = validationErrors;
            if (!field.props.onChange) {
                props.onChange = this.handleFieldChange;
            }
            if (fieldKey) {
                props.ref = fieldKey;
            }
        }

        if (field.props.type === 'commonErrors') {
            if (validationErrors.common)
                props.children = field.props.children || validationErrors.common;
        }

        return React.cloneElement(field, props);
    };

    handleFieldChange = (fieldKey) => (...args) => {
        const {onFieldChange} = this.props;
        onFieldChange(fieldKey, ...args);

        this.setState({
            editedAfterSubmit: true
        });
    };

    handleSubmit = event => {
        event && event.preventDefault();
        event && event.stopPropagation();

        if (this.hasClientValidationErrors()) {
            this.props.onErrors();
            this.setState({
                wasSubmit: true
            });
            return;
        }

        this.setState({
            wasSubmit: true,
            editedAfterSubmit: false,
            hideEmptyErrorAfterSubmit: true
        });

        const {onSubmit, fieldValues} = this.props;
        onSubmit(fieldValues);
    };

    clearForm() {
        this.setState({
            wasSubmit: false,
            editedAfterSubmit: false
        })
    }

    handleKeyDown = event => {
        if (event.key !== 'Tab')
            return;
        event.preventDefault();
        const elements = [
            ...this.refs.form.querySelectorAll('[role=textbox]'),
            ...this.refs.form.elements
        ].sort(this.sortByDOMPosition);
        const elementsLength = elements.length;
        for (let i = 0; i < elementsLength; i++) {
            const element = elements[i];
            if (element === event.target) {
                const el = this.getNextField(i, elements, elementsLength);
                el.focus();
                if (!el.value || el.type !== 'number') {
                    return;
                }
                el.type = 'text';
                const len = el.value.length;
                el.setSelectionRange(len, len);
                el.type = 'number';
            }
        }
    };

    sortByDOMPosition = (a, b) => {
        if (a === b) return 0;
        if (!a.compareDocumentPosition) {
            return a.sourceIndex - b.sourceIndex;
        }
        if (a.compareDocumentPosition(b) & 2) {
            return 1;
        }
        return -1;
    }

    getNextField = (index, elements, length) => {
        const newIndex = index + 1 === length ? 0 : index + 1;
        const element = elements[newIndex];
        if (!element || element.hidden || element.disabled) {
            return this.getNextField(newIndex, elements, length)
        }
        return element;
    }
}



// WEBPACK FOOTER //
// ./src/components/controls/form/Form.jsx